import { checkSession } from "@features/auth";
import { authClient } from "@features/auth/authClient";
import { useUserInfo } from "@features/auth/useAuth";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { Button } from "@shadcn/ui/button";
import { Input } from "@shadcn/ui/input";
import { Label } from "@shadcn/ui/label";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { Eye, EyeClosed } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { AppHeader } from "~/components/layout/AppHeader";
import { PageContainer } from "~/components/layout/PageContainer";

export const Route = createFileRoute("/profile/edit-profile")({
	beforeLoad: () => {
		document.title = 'bacehub | Startseite'
	},
	component: RouteComponent,
	loader: async () => {
		const sessionExists = await checkSession();

		if (!sessionExists) {
			throw redirect({
				to: "/",
			});
		}
	},
});

export default function RouteComponent() {
	const router = useRouter();
	const user = useUserInfo();
	const i18n = useI18nContext().LL["new-ui"];

	// Name editing state
	const [isEditingName, setIsEditingName] = useState(false);
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [isUpdating, setIsUpdating] = useState(false);

	// Password editing state
	const [isEditingPassword, setIsEditingPassword] = useState(false);
	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [isChangingPassword, setIsChangingPassword] = useState(false);
	const [showCurrentPassword, setShowCurrentPassword] = useState(false);
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);

	if (!user) {
		return null;
	}

	// Check if user is authenticated with Google by looking at avatar URL
	const isGoogleAccount = user.avatar?.includes("googleusercontent.com") || user.avatar?.includes("google");

	// Build display name from firstName and lastName or use name
	const displayName =
		user.firstName || user.lastName
			? `${user.firstName || ""} ${user.lastName || ""}`.trim()
			: user.name || i18n.PROFILE_EDIT_NOT_PROVIDED();

	const handleUpdateName = async () => {
		if (!firstName.trim() && !lastName.trim()) {
			toast.error(i18n.PROFILE_EDIT_NAME_REQUIRED());
			return;
		}

		setIsUpdating(true);
		try {
			// Update user info using Better Auth
			await authClient.updateUser({
				name: `${firstName} ${lastName}`.trim(),
				firstName: firstName.trim() || undefined,
				lastName: lastName.trim() || undefined,
			});

			// Refetch user session to get updated data
			await authClient.getSession();

			toast.success(i18n.PROFILE_EDIT_NAME_UPDATED());
			setIsEditingName(false);
			setFirstName("");
			setLastName("");

			// Refresh the route to get updated user data
			router.invalidate();
		} catch (error) {
			toast.error(i18n.PROFILE_EDIT_NAME_FAILED());
			console.error("Error updating name:", error);
		} finally {
			setIsUpdating(false);
		}
	};

	const handleChangePassword = async () => {
		if (!currentPassword.trim() || !newPassword.trim() || !confirmPassword.trim()) {
			toast.error(i18n.PROFILE_EDIT_PASSWORD_REQUIRED());
			return;
		}

		if (newPassword !== confirmPassword) {
			toast.error(i18n.PROFILE_EDIT_PASSWORD_MISMATCH());
			return;
		}

		setIsChangingPassword(true);
		try {
			const { error } = await authClient.changePassword({
				currentPassword,
				newPassword,
				revokeOtherSessions: true,
			});

			if (error) {
				toast.error(error.message || i18n.PROFILE_EDIT_PASSWORD_FAILED());
				return;
			}

			toast.success(i18n.PROFILE_EDIT_PASSWORD_UPDATED());
			setIsEditingPassword(false);
			setCurrentPassword("");
			setNewPassword("");
			setConfirmPassword("");
			setShowCurrentPassword(false);
			setShowNewPassword(false);
			setShowConfirmPassword(false);
		} catch (error) {
			toast.error(
				error instanceof Error
					? error.message
					: i18n.PROFILE_EDIT_PASSWORD_FAILED()
			);
			console.error("Error changing password:", error);
		} finally {
			setIsChangingPassword(false);
		}
	};

	const renderNameSection = () => {
		if (isEditingName) {
			return (
				<div className="flex w-full flex-col gap-6 border-t py-5">
					<div className="flex flex-col gap-4">
						<p className="text-base font-medium font-breit text-card-foreground">
							{i18n.PROFILE_EDIT_NAME()}
						</p>
						<div className="flex flex-col gap-2">
							<Label htmlFor="firstName" className="text-sm font-breit text-foreground">
								<T>{i18n.PROFILE_EDIT_FIRST_NAME()}</T>
							</Label>
							<Input
								id="firstName"
								type="text"
								placeholder={i18n.PROFILE_EDIT_FIRST_NAME()}
								value={firstName}
								onChange={(e) => setFirstName(e.target.value)}
								disabled={isUpdating}
							/>
						</div>
						<div className="flex flex-col gap-2">
							<Label htmlFor="lastName" className="text-sm font-breit text-foreground">
								<T>{i18n.PROFILE_EDIT_LAST_NAME()}</T>
							</Label>
							<Input
								id="lastName"
								type="text"
								placeholder={i18n.PROFILE_EDIT_LAST_NAME()}
								value={lastName}
								onChange={(e) => setLastName(e.target.value)}
								disabled={isUpdating}
							/>
						</div>
					</div>
					<div className="flex flex-col gap-4">
						<Button onClick={handleUpdateName} disabled={isUpdating || (!firstName.trim() && !lastName.trim())}>
							<T>{i18n.PROFILE_EDIT_SAVE()}</T>
						</Button>
						<Button
							variant="ghost"
							onClick={() => {
								setIsEditingName(false);
								setFirstName("");
								setLastName("");
							}}
						>
							<T>{i18n.CANCEL()}</T>
						</Button>
					</div>
				</div>
			);
		}

		return (
			<div className="flex flex-col gap-4 py-5 border-t">
				<div className="flex flex-col gap-2">
					<p className="text-base font-medium font-breit text-card-foreground">
						{i18n.PROFILE_EDIT_NAME()}
					</p>
					<p className="text-base text-muted-foreground">{displayName}</p>
				</div>
				<Button
					variant="outline"
					className="w-full"
					onClick={() => {
						setFirstName(user.firstName || "");
						setLastName(user.lastName || "");
						setIsEditingName(true);
					}}
					disabled={isEditingPassword}
				>
					{displayName === i18n.PROFILE_EDIT_NOT_PROVIDED()
						? i18n.PROFILE_EDIT_ADD_NAME()
						: i18n.PROFILE_EDIT_EDIT_NAME()}
				</Button>
			</div>
		);
	};

	const renderEmailSection = () => (
		<div className="flex flex-col gap-4 py-5 border-t">
			<div className="flex flex-col gap-2">
				<p className="text-base font-medium font-breit text-card-foreground">
					{i18n.PROFILE_EDIT_EMAIL()}
				</p>
				<p className="text-base text-muted-foreground">{user.email}</p>
			</div>
			<Button
				variant="outline"
				className="w-full"
				onClick={() => toast.info(i18n.PROFILE_EDIT_EMAIL_COMING_SOON())}
				disabled={isEditingName || isEditingPassword}
			>
				{i18n.PROFILE_EDIT_EDIT_EMAIL()}
			</Button>
		</div>
	);

	const renderPasswordSection = () => {
		if (isEditingPassword) {
			return (
				<div className="flex w-full flex-col gap-6 border-t py-5">
					<div className="flex flex-col gap-4">
						<p className="text-base font-medium font-breit text-card-foreground">
							{i18n.PROFILE_EDIT_PASSWORD()}
						</p>
						<div className="flex flex-col gap-2">
							<Label htmlFor="currentPassword" className="text-sm font-breit text-foreground">
								<T>{i18n.PROFILE_EDIT_CURRENT_PASSWORD()}</T>
							</Label>
							<div className="relative">
								<Input
									id="currentPassword"
									type={showCurrentPassword ? "text" : "password"}
									placeholder={i18n.PROFILE_EDIT_CURRENT_PASSWORD()}
									value={currentPassword}
									onChange={(e) => setCurrentPassword(e.target.value)}
									disabled={isChangingPassword}
									className="pr-10"
								/>
								<button
									type="button"
									className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
									onClick={() => setShowCurrentPassword(!showCurrentPassword)}
									tabIndex={-1}
								>
									{showCurrentPassword ? <Eye className="size-4" /> : <EyeClosed className="size-4" />}
								</button>
							</div>
						</div>
						<div className="flex flex-col gap-2">
							<Label htmlFor="newPassword" className="text-sm font-breit text-foreground">
								<T>{i18n.PROFILE_EDIT_NEW_PASSWORD()}</T>
							</Label>
							<div className="relative">
								<Input
									id="newPassword"
									type={showNewPassword ? "text" : "password"}
									placeholder={i18n.PROFILE_EDIT_NEW_PASSWORD()}
									value={newPassword}
									onChange={(e) => setNewPassword(e.target.value)}
									disabled={isChangingPassword}
									className="pr-10"
								/>
								<button
									type="button"
									className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
									onClick={() => setShowNewPassword(!showNewPassword)}
									tabIndex={-1}
								>
									{showNewPassword ? <Eye className="size-4" /> : <EyeClosed className="size-4" />}
								</button>
							</div>
						</div>
						<div className="flex flex-col gap-2">
							<Label htmlFor="confirmPassword" className="text-sm font-breit text-foreground">
								<T>{i18n.PROFILE_EDIT_CONFIRM_PASSWORD()}</T>
							</Label>
							<div className="relative">
								<Input
									id="confirmPassword"
									type={showConfirmPassword ? "text" : "password"}
									placeholder={i18n.PROFILE_EDIT_CONFIRM_PASSWORD()}
									value={confirmPassword}
									onChange={(e) => setConfirmPassword(e.target.value)}
									disabled={isChangingPassword}
									className="pr-10"
								/>
								<button
									type="button"
									className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
									onClick={() => setShowConfirmPassword(!showConfirmPassword)}
									tabIndex={-1}
								>
									{showConfirmPassword ? <Eye className="size-4" /> : <EyeClosed className="size-4" />}
								</button>
							</div>
						</div>
					</div>
					<div className="flex flex-col gap-4">
						<Button
							onClick={handleChangePassword}
							disabled={isChangingPassword || !currentPassword.trim() || !newPassword.trim() || !confirmPassword.trim()}
						>
							<T>{i18n.PROFILE_EDIT_SAVE()}</T>
						</Button>
						<Button
							variant="ghost"
							onClick={() => {
								setIsEditingPassword(false);
								setCurrentPassword("");
								setNewPassword("");
								setConfirmPassword("");
								setShowCurrentPassword(false);
								setShowNewPassword(false);
								setShowConfirmPassword(false);
							}}
						>
							<T>{i18n.CANCEL()}</T>
						</Button>
					</div>
				</div>
			);
		}

		return (
			<div className="flex flex-col gap-4 py-5 border-t">
				<div className="flex flex-col gap-2">
					<p className="text-base font-medium font-breit text-card-foreground">
						{i18n.PROFILE_EDIT_PASSWORD()}
					</p>
				</div>
				<Button
					variant="outline"
					className="w-full"
					onClick={() => setIsEditingPassword(true)}
					disabled={isEditingName}
				>
					{i18n.PROFILE_EDIT_CHANGE_PASSWORD()}
				</Button>
			</div>
		);
	};

	const renderLinkedAccountSection = () => (
		<div className="py-5 border-t flex flex-col gap-4">
			<div className="flex flex-col gap-2">
				<p className="text-base font-medium font-breit text-card-foreground">
					{i18n.PROFILE_EDIT_LINKED_ACCOUNT()}
				</p>
			</div>
			<div className="flex items-center gap-3">
				{user.avatar ? (
					<div className="w-10 h-10 rounded-full overflow-hidden bg-muted flex-shrink-0">
						<img src={user.avatar} alt="Profile" className="w-full h-full object-cover" />
					</div>
				) : (
					<div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
						<span className="text-sm font-medium text-muted-foreground">
							{user.name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase() || "?"}
						</span>
					</div>
				)}
				<div className="flex flex-col">
					<p className="text-base font-medium text-foreground">
						<T>{i18n.PROFILE_EDIT_GOOGLE_ACCOUNT()}</T>
					</p>
					<p className="text-base text-muted-foreground">{user.email}</p>
				</div>
			</div>
		</div>
	);

	const renderDeleteAccountSection = () => (
		<div className="flex flex-col gap-4 py-5 border-t">
			<div className="flex flex-col gap-2">
				<p className="text-base font-medium font-breit text-card-foreground">
					{i18n.PROFILE_EDIT_DELETE_ACCOUNT()}
				</p>
			</div>
			<Button
				variant="destructive"
				className="w-full"
				onClick={() => toast.info(i18n.PROFILE_EDIT_DELETE_COMING_SOON())}
				disabled={isEditingName || isEditingPassword}
			>
				{i18n.PROFILE_EDIT_DELETE_ACCOUNT()}
			</Button>
		</div>
	);

	return (
		<PageContainer>
			<AppHeader service="ALL" />
			<h1 className="text-2xl font-breit px-6 py-4">
				<T>{i18n.PROFILE_EDIT_PROFILE()}</T>
			</h1>
			<div className="px-6 flex flex-col">
				{isGoogleAccount ? (
					<>
						{renderNameSection()}
						{renderLinkedAccountSection()}
						{renderDeleteAccountSection()}
					</>
				) : (
					<>
						{renderNameSection()}
						{renderEmailSection()}
						{renderPasswordSection()}
						{renderDeleteAccountSection()}
					</>
				)}
			</div>
		</PageContainer>
	);
}
